import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import PartnersCarousel from "@/components/products/PartnersCarousel";
import { FAQSection } from "@/components/loan/FAQSection";

const UKKPage = () => {
  const isMobile = useIsMobile();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  return (
    <div className="bg-blue-50 min-h-screen">
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-8 lg:py-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="mb-6 md:mb-10"
        >
          <motion.h1 
            variants={itemVariants}
            className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-6 text-blue-700 text-center"
          >
            Usein Kysytyt Kysymykset
          </motion.h1>
          
          <motion.div 
            variants={itemVariants}
            className="bg-white p-4 md:p-6 rounded-lg mb-4 md:mb-8 shadow-sm"
          >
            <h2 className="text-base md:text-lg lg:text-xl font-semibold mb-2 md:mb-4 text-blue-700 text-left">Yleisimmät kysymykset Lainasampo.fi-palvelusta</h2>
            <p className="text-sm md:text-base text-gray-700 text-left">
              Olemme koonneet tähän vastauksia asiakkaidemme useimmin kysymiin kysymyksiin. 
              Jos et löydä vastausta kysymykseesi, ota yhteyttä asiakaspalveluumme.
            </p>
          </motion.div>
        </motion.div>
        
        {isMobile && (
          <div className="mb-6">
            <Link to="/kilpailuta">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg shadow-sm text-sm md:text-base">
                Kilpailuta lainasi nyt
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2">
            <div className="mb-5 md:mb-8">
              <h3 className="text-base md:text-lg lg:text-xl font-semibold mb-2 md:mb-4 text-blue-700 text-left">Palvelun käyttäminen</h3>
              <Accordion type="single" collapsible className="bg-white rounded-lg border shadow-sm">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Miten Lainasampo.fi-palvelu toimii?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Lainasampo.fi on lainojen vertailupalvelu, jonka avulla voit kilpailuttaa lainasi yhdellä hakemuksella. 
                    Täytät yhden hakemuksen, jonka lähetämme useille yhteistyöpankeillemme. Saat vertailukelpoiset 
                    lainatarjoukset, joista voit valita itsellesi sopivimman.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Onko palvelun käyttäminen maksutonta?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Kyllä, palvelumme on täysin maksuton käyttäjille. Saamme tuottomme yhteistyöpankeiltamme, 
                    jos päädyt ottamaan lainan heidän kauttaan.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Mitkä pankit ja rahoituslaitokset ovat yhteistyökumppaneitanne?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Teemme yhteistyötä useiden suomalaisten ja kansainvälisten pankkien ja rahoituslaitosten kanssa. 
                    Yhteistyökumppaneitamme ovat mm. suurimmat suomalaiset pankit sekä useat erikoistuneet luotonantajat. 
                    Ajantasaisen listan löydät palvelustamme lainahakemuksen yhteydessä.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            <div className="mb-5 md:mb-8">
              <h3 className="text-base md:text-lg lg:text-xl font-semibold mb-2 md:mb-4 text-blue-700 text-left">Lainat ja rahoitus</h3>
              <Accordion type="single" collapsible className="bg-white rounded-lg border shadow-sm">
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Minkä suuruisia lainoja voin hakea?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Palvelumme kautta voit hakea lainoja 2 000 eurosta ylöspäin. Kulutusluottojen osalta maksimimäärä 
                    on tyypillisesti 25 000 - 75 000 euroa, riippuen pankin ehdoista. Asuntolainoissa ja yrityslainoissa 
                    summat voivat olla huomattavasti suurempia.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Mitkä tekijät vaikuttavat lainan korkoon?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Lainan korkoon vaikuttavat monet tekijät, kuten lainan määrä ja laina-aika, 
                    oma luottoluokituksesi ja taloudellinen tilanteesi, lainan vakuudet sekä 
                    yleinen markkinatilanne. Eri pankit painottavat näitä tekijöitä eri tavoin 
                    koron määrittelyssä.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-6">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Kuinka nopeasti saan rahat tililleni?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Nopeus vaihtelee lainan tyypistä ja pankista riippuen. Kulutusluottojen kohdalla 
                    voit saada rahat tilillesi jopa saman päivän aikana. Asuntolainoissa prosessi kestää 
                    tyypillisesti 1-2 viikkoa. Yrityslainoissa käsittelyaika on yleensä muutamasta päivästä 
                    viikkoihin riippuen lainan suuruudesta ja yrityksen tilanteesta.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            <div className="mb-5 md:mb-8">
              <h3 className="text-base md:text-lg lg:text-xl font-semibold mb-2 md:mb-4 text-blue-700 text-left">Hakeminen ja ehdot</h3>
              <Accordion type="single" collapsible className="bg-white rounded-lg border shadow-sm">
                <AccordionItem value="item-7">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Mitä tietoja minun tulee antaa hakemuksessani?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Lainahakemuksessa kysytään tyypillisesti henkilötietojasi, tulotietojasi, tietoja nykyisistä 
                    veloistasi ja mahdollisista luotoistasi sekä tietoa haettavan lainan määrästä ja käyttötarkoituksesta. 
                    Kaikki tiedot käsitellään turvallisesti ja luottamuksellisesti.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-8">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Tarkistetaanko luottotietoni hakemuksen yhteydessä?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Kyllä, pankit ja rahoituslaitokset tarkistavat luottotietosi osana lainahakemuksen käsittelyä. 
                    Tämä on normaali osa lainanantoprosessia. Lainasampo.fi ei itse tee luottotietokyselyä, 
                    vaan tarkistuksen tekevät yhteistyöpankkimme.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-9">
                  <AccordionTrigger className="text-blue-700 font-medium px-3 md:px-4 py-3 text-sm md:text-base text-left">
                    Mitä jos en ole tyytyväinen saamiini tarjouksiin?
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-gray-700 text-left">
                    Sinulla ei ole mitään velvollisuutta hyväksyä mitään saamaasi tarjousta. Voit vapaasti hylätä 
                    kaikki tarjoukset, jos ne eivät vastaa tarpeitasi. Voit myös ottaa yhteyttä asiakaspalveluumme, 
                    joka voi auttaa sinua löytämään parempia vaihtoehtoja.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {isMobile && (
              <div className="mb-6">
                <Link to="/kilpailuta">
                  <Button className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg shadow-sm text-sm md:text-base">
                    Kilpailuta lainasi nyt
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
          
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-5 mb-4 md:mb-6 border border-blue-100">
              <h3 className="text-base md:text-lg font-semibold text-blue-700 mb-3 text-left">Säästä rahaa kilpailuttamalla lainasi</h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 text-left">Kilpailuttamalla lainasi voit säästää jopa tuhansia euroja laina-ajallasi. Täytä yksi hakemus ja saat useita tarjouksia!</p>
              
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to="/kilpailuta">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 md:py-3 text-sm md:text-base">
                    Kilpailuta lainasi nyt
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                </Link>
              </motion.div>
            </div>
            
            <div className="bg-green-50 rounded-lg shadow-sm p-4 md:p-5 mb-4 md:mb-6 border border-green-100">
              <h3 className="text-base md:text-lg font-semibold text-green-700 mb-3 text-left">Asiakkaiden kokemuksia</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-3 py-1">
                  <p className="text-xs md:text-sm text-gray-700 italic text-left">
                    "Säästin yli 2000€ kilpailuttamalla asuntolainani Lainasampo.fi:n kautta!"
                  </p>
                  <p className="text-xs md:text-sm text-green-600 font-medium mt-1 text-left">- Matti K., Helsinki</p>
                </div>
                <div className="border-l-4 border-green-500 pl-3 py-1">
                  <p className="text-xs md:text-sm text-gray-700 italic text-left">
                    "Nopea ja helppo tapa vertailla lainatarjouksia. Suosittelen!"
                  </p>
                  <p className="text-xs md:text-sm text-green-600 font-medium mt-1 text-left">- Liisa M., Tampere</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-600 text-white p-4 md:p-5 lg:p-7 rounded-lg my-5 md:my-8 shadow-md"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0 md:mr-8">
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-3 text-left">Etkö löytänyt vastausta kysymykseesi?</h2>
              <p className="text-sm md:text-base text-blue-50 text-left">
                Asiakaspalvelumme auttaa sinua mielellään kaikissa lainoihin liittyvissä kysymyksissä.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <Link 
                to="/yhteydenotto" 
                className="bg-white text-blue-600 px-4 py-2 md:px-5 md:py-3 rounded-md font-medium hover:bg-blue-50 transition-colors text-center text-sm md:text-base"
              >
                Ota yhteyttä
              </Link>
              <Link 
                to="/kilpailuta" 
                className="bg-green-500 text-white px-4 py-2 md:px-5 md:py-3 rounded-md font-medium hover:bg-green-600 transition-colors text-center text-sm md:text-base"
              >
                Kilpailuta lainasi
              </Link>
            </div>
          </div>
        </motion.div>
        
        <div className="my-5 md:my-8">
          <FAQSection />
        </div>
      </div>
      
      <div className="bg-white py-6 md:py-8">
        <PartnersCarousel altTitle="Yhteistyöpankkimme" />
      </div>
    </div>
  );
};

export default UKKPage;
